import React from "react";
import { Guid } from "guid-typescript";
import { createPortal } from "react-dom";

import "./DatePicker.scss";

enum DayNames {
    Sunday,
    Monday,
    Tuesday,
    Wednesday,
    Thursday,
    Friday,
    Saturday,
}

enum MonthNames {
    January,
    February,
    March,
    April,
    May,
    June,
    July,
    August,
    September,
    October,
    November,
    December,
}

interface DatePickerProps {
    id?: string;
    label?: string;
    value?: Date;
    width?: number | string;
    minDate?: Date;
    maxDate?: Date;
    disabled?: boolean;
    className?: string;
    tooltip?: string;
    tooltipPlace?: string;
    onChange?: (value: Date) => void;
}

interface DatePickerState {
    contRef: React.RefObject<HTMLLabelElement>;
    ref: React.RefObject<HTMLInputElement>;
    yearRef: React.RefObject<HTMLLIElement>;
    toggleRef: React.RefObject<HTMLButtonElement>;
    anchor: string;
    open: boolean;
    setMonth: boolean;
    setYear: boolean;
    move: number;
    focused: boolean;
    displayValue: string;
    openMonth: Date;
    current: Date;
    monthLast: Array<
        Record<
            string,
            {
                date: number;
                current: boolean;
                selected: boolean;
                disabled: boolean;
            }
        >
    >;
    monthCurrent: Array<
        Record<
            string,
            {
                date: number;
                current: boolean;
                selected: boolean;
                disabled: boolean;
            }
        >
    >;
    monthNext: Array<
        Record<
            string,
            {
                date: number;
                current: boolean;
                selected: boolean;
                disabled: boolean;
            }
        >
    >;
}

class DatePicker extends React.Component<DatePickerProps, DatePickerState> {
    state = {
        contRef: React.createRef<HTMLLabelElement>(),
        ref: React.createRef<HTMLInputElement>(),
        yearRef: React.createRef<HTMLLIElement>(),
        toggleRef: React.createRef<HTMLButtonElement>(),
        anchor: `--${Guid.create().toString()}`,
        open: false,
        move: 0,
        setMonth: false,
        setYear: false,
        focused: false,
        displayValue: "",
        current: new Date(),
        openMonth: new Date(),
        monthLast: [],
        monthCurrent: [],
        monthNext: [],
    };

    static getDerivedStateFromProps(
        props: DatePickerProps,
        state: DatePickerState,
    ): Partial<DatePickerState> {
        if (props.value !== state.openMonth) {
            return {
                displayValue:
                    props.value?.toDate()?.toLocale() ?? "",
            };
        }
        return null;
    }

    componentDidMount(): void {}

    componentDidUpdate(
        prevProps: Readonly<DatePickerProps>,
        prevState: Readonly<DatePickerState>,
        snapshot?: any,
    ): void {
        if (this.state.open && prevProps.value !== this.props.value) {
            this.open();
        }
    }

    open() {
        const openMonth = (this.props.value || new Date()).dateOnly();
        const lastMonth = this.buildMonth(openMonth, -1);
        const currentMonth = this.buildMonth(openMonth, 0);
        const nextMonth = this.buildMonth(openMonth, 1);
        this.setState({
            open: true,
            openMonth: openMonth,
            monthLast: lastMonth,
            monthCurrent: currentMonth,
            monthNext: nextMonth,
        });
    }

    close() {
        this.setState({ open: false });
    }

    toggle() {
        if (this.props.disabled) return;
        if (this.state.open) this.close();
        else this.open();
    }

    toggleMonths() {
        if (this.state.setMonth || this.state.setYear) {
            this.setState({ setMonth: false, setYear: false });
        } else {
            this.setState({ setYear: true, setMonth: false });
            setTimeout(() =>
                this.state.yearRef.current?.scrollIntoView({
                    block: "center",
                }),
            );
        }
        this.handleFocus();
    }

    setMonth(index: number) {
        const openMonth = this.state.openMonth.addMonths(index);
        const lastMonth = this.buildMonth(openMonth, -1);
        const currentMonth = this.buildMonth(openMonth, 0);
        const nextMonth = this.buildMonth(openMonth, 1);
        this.setState({
            openMonth,
            monthLast: lastMonth,
            monthCurrent: currentMonth,
            monthNext: nextMonth,
            move: 0,
        });
    }

    nextMonth() {
        this.setState({ move: 1 });
        setTimeout(() => this.setMonth(1), 300);
        this.handleFocus();
    }

    previousMonth() {
        this.setState({ move: -1 });
        setTimeout(() => this.setMonth(-1), 300);
        this.handleFocus();
    }

    handleFocus() {
        setTimeout(() => {
            this.setState({ focused: true });
        });
    }

    opId: string;
    handleBlur() {
        const opId = Guid.create().toString();
        this.opId = opId;
        this.setState({ focused: false });
        setTimeout(() => {
            if (!this.state.focused && this.opId === opId) {
                this.close();
            }
        }, 200);
    }

    handleClick(event: React.MouseEvent<HTMLInputElement>) {
        if (this.props.disabled || event.target !== this.state.ref.current)
            return;
        this.toggle();
    }

    handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
        switch (event.key) {
            case "Enter":
            case " ":
            case "Escape":
                event.stopPropagation();
                event.preventDefault();
                break;
        }
    }

    handleKeyUp(event: React.KeyboardEvent<HTMLInputElement>) {
        switch (event.key) {
            case "Enter":
            case " ":
                this.toggle();
                event.stopPropagation();
                event.preventDefault();
                break;
            case "Escape":
                this.close();
                event.stopPropagation();
                event.preventDefault();
                break;
        }
    }

    buildMonth(date: Date, index: number) {
        const result = [];
        const monthDate = date.addMonths(index);
        const month = monthDate.getMonth();
        let currentDate = monthDate;
        currentDate.setDate(1);
        let currentRow: Record<
            string,
            {
                date: number;
                current: boolean;
                selected: boolean;
                disabled: boolean;
            }
        >;

        while (currentDate.getMonth() === month) {
            const day = currentDate.getDay();
            if (!currentRow || day === 0) {
                currentRow = {};
                result.push(currentRow);
            }
            currentRow[DayNames[day]] = {
                date: currentDate.getDate(),
                current: currentDate.equalsDate(this.state.current),
                selected:
                    this.props.value &&
                    currentDate.equalsDate(this.props.value),
                disabled:
                    (this.props.minDate &&
                        this.props.maxDate &&
                        !currentDate.betweenDates(
                            this.props.minDate,
                            this.props.maxDate,
                        )) ||
                    (this.props.minDate &&
                        currentDate.compareDate(this.props.minDate) < 0) ||
                    (this.props.maxDate &&
                        currentDate.compareDate(this.props.maxDate) > 0),
            };
            currentDate = currentDate.addDays(1);
        }

        return result;
    }

    getMonthName(month: number) {
        return MonthNames[month];
    }

    setDisplayMonth(
        event:
            | React.MouseEvent<HTMLLIElement>
            | React.KeyboardEvent<HTMLLIElement>,
        month: number,
    ) {
        this.state.toggleRef.current?.focus();
        let value = new Date(this.props.value?.valueOf());
        value.setMonth(month);
        if (this.props.minDate && value.compareDate(this.props.minDate) < 0)
            value = new Date(this.props.minDate.valueOf());
        if (this.props.maxDate && value.compareDate(this.props.maxDate) > 0)
            value = new Date(this.props.maxDate.valueOf());
        this.setState({ setMonth: false });
        this.props.onChange?.(value);
        event.stopPropagation();
        event.preventDefault();
    }

    setDisplayedYear(
        event:
            | React.MouseEvent<HTMLLIElement>
            | React.KeyboardEvent<HTMLLIElement>,
        year: number,
    ) {
        let value = new Date(this.props.value?.valueOf());
        value.setFullYear(year);
        if (this.props.minDate && value.compareDate(this.props.minDate) < 0)
            value = new Date(this.props.minDate.valueOf());
        if (this.props.maxDate && value.compareDate(this.props.maxDate) > 0)
            value = new Date(this.props.maxDate.valueOf());
        this.setState({ setYear: false, setMonth: true });
        this.props.onChange?.(value);
        this.handleFocus();
        event.stopPropagation();
        event.preventDefault();
    }

    setSelectedDate(
        event:
            | React.MouseEvent<HTMLLIElement>
            | React.KeyboardEvent<HTMLLIElement>,
        date: number,
    ) {
        const value = new Date(
            this.state.openMonth.getFullYear(),
            this.state.openMonth.getMonth(),
            date,
        );
        this.props.onChange?.(value);
        this.close();
        this.state.ref.current?.focus();
        event.stopPropagation();
        event.preventDefault();
    }

    render(): React.ReactNode {
        const rect = this.state.contRef.current?.getBoundingClientRect();
        const isTop = (rect ? rect.top / window.innerHeight : 0) > 0.5;

        let classes = "date-picker";

        if (this.props.className) classes += ` ${this.props.className}`;
        if (this.props.disabled) classes += " disabled";

        return (
            <label
                ref={this.state.contRef}
                className={classes}
                style={{
                    width: this.props.width,
                    anchorName: this.state.anchor,
                }}
                data-html={true}
                data-tip={this.props.tooltip}
                data-place={this.props.tooltipPlace}>
                <input
                    ref={this.state.ref}
                    type="text"
                    readOnly={true}
                    disabled={this.props.disabled}
                    value={this.state.displayValue}
                    onFocus={this.handleFocus.bind(this)}
                    onBlur={this.handleBlur.bind(this)}
                    onClick={this.handleClick.bind(this)}
                    onKeyDown={this.handleKeyDown.bind(this)}
                    onKeyUp={this.handleKeyUp.bind(this)}
                />
                {this.props.label && (
                    <span className="text">{this.props.label}</span>
                )}
                {this.state.open &&
                    createPortal(
                        <div
                            className={`dates-display${isTop ? " top" : ""}`}
                            style={{
                                positionAnchor: this.state.anchor,
                            }}>
                            <span className="date-header">
                                <span className="month-year">
                                    <button
                                        ref={this.state.toggleRef}
                                        className={`${
                                            this.state.setYear ||
                                            this.state.setMonth
                                                ? "expanded"
                                                : ""
                                        }`}
                                        onFocus={this.handleFocus.bind(this)}
                                        onClick={this.toggleMonths.bind(this)}>
                                        <i>expand_more</i>
                                    </button>
                                    <span
                                        className={`${
                                            this.state.setMonth ||
                                            this.state.setYear
                                                ? "hide"
                                                : ""
                                        }`}>
                                        {this.getMonthName(
                                            this.state.openMonth.getMonth(),
                                        )}
                                    </span>
                                    <span
                                        className={`${
                                            this.state.setYear ? "hide" : ""
                                        }`}>
                                        {this.state.openMonth.getFullYear()}
                                    </span>
                                </span>
                                <span className="month-buttons">
                                    <button
                                        onFocus={this.handleFocus.bind(this)}
                                        onClick={this.previousMonth.bind(this)}>
                                        <i>chevron_left</i>
                                    </button>
                                    <button
                                        onFocus={this.handleFocus.bind(this)}
                                        onClick={this.nextMonth.bind(this)}>
                                        <i>chevron_right</i>
                                    </button>
                                </span>
                            </span>
                            <div className="calendar-section">
                                <ul className="days">
                                    <li>S</li>
                                    <li>M</li>
                                    <li>T</li>
                                    <li>W</li>
                                    <li>T</li>
                                    <li>F</li>
                                    <li>S</li>
                                </ul>
                            </div>
                            <div
                                className={`calendars${
                                    this.state.move === -1 ? " prev" : ""
                                }${this.state.move === 1 ? " next" : ""}`}>
                                <div className="calendar-section month-last">
                                    {this.state.monthLast.map((row, index) => (
                                        <ul
                                            key={index}
                                            className="days">
                                            {Object.keys(DayNames)
                                                .filter(key =>
                                                    isNaN(Number(key)),
                                                )
                                                .map(day => (
                                                    <li
                                                        key={day}
                                                        className={`${
                                                            row[day]?.current
                                                                ? "current"
                                                                : ""
                                                        }${
                                                            row[day]?.selected
                                                                ? " selected"
                                                                : ""
                                                        }${
                                                            row[day]?.disabled
                                                                ? " disabled"
                                                                : ""
                                                        }`}>
                                                        {row[day]?.date}
                                                    </li>
                                                ))}
                                        </ul>
                                    ))}
                                </div>
                                <div className="calendar-section month-current">
                                    {this.state.monthCurrent.map(
                                        (row, index) => (
                                            <ul
                                                key={index}
                                                className="days">
                                                {Object.keys(DayNames)
                                                    .filter(key =>
                                                        isNaN(Number(key)),
                                                    )
                                                    .map(day => (
                                                        <li
                                                            key={day}
                                                            tabIndex={
                                                                !(
                                                                    this.state
                                                                        .setMonth ||
                                                                    this.state
                                                                        .setYear
                                                                ) &&
                                                                row[day]?.date
                                                                    ? 0
                                                                    : -1
                                                            }
                                                            className={`${
                                                                row[day]?.date
                                                                    ? "available"
                                                                    : ""
                                                            }${
                                                                row[day]
                                                                    ?.current
                                                                    ? " current"
                                                                    : ""
                                                            }${
                                                                row[day]
                                                                    ?.selected
                                                                    ? " selected"
                                                                    : ""
                                                            }${
                                                                row[day]
                                                                    ?.disabled
                                                                    ? " disabled"
                                                                    : ""
                                                            }`}
                                                            onFocus={this.handleFocus.bind(
                                                                this,
                                                            )}
                                                            onKeyUpCapture={event =>
                                                                event.key ===
                                                                    "Enter" &&
                                                                this.setSelectedDate(
                                                                    event,
                                                                    row[day]
                                                                        .date,
                                                                )
                                                            }
                                                            onClickCapture={event =>
                                                                this.setSelectedDate(
                                                                    event,
                                                                    row[day]
                                                                        .date,
                                                                )
                                                            }>
                                                            {row[day]?.date}
                                                        </li>
                                                    ))}
                                            </ul>
                                        ),
                                    )}
                                </div>
                                <div className="calendar-section month-next">
                                    {this.state.monthNext.map((row, index) => (
                                        <ul
                                            key={index}
                                            className="days">
                                            {Object.keys(DayNames)
                                                .filter(key =>
                                                    isNaN(Number(key)),
                                                )
                                                .map(day => (
                                                    <li
                                                        key={day}
                                                        className={`${
                                                            row[day]?.current
                                                                ? "current"
                                                                : ""
                                                        }${
                                                            row[day]?.selected
                                                                ? " selected"
                                                                : ""
                                                        }${
                                                            row[day]?.disabled
                                                                ? " disabled"
                                                                : ""
                                                        }`}>
                                                        {row[day]?.date}
                                                    </li>
                                                ))}
                                        </ul>
                                    ))}
                                </div>
                            </div>
                            <div
                                className={`calendar-months${
                                    this.state.setMonth ? " open" : ""
                                }`}>
                                <ul className="months">
                                    {Object.keys(MonthNames)
                                        .filter(key => isNaN(Number(key)))
                                        .map(month => (
                                            <li
                                                key={month}
                                                tabIndex={0}
                                                className={`${
                                                    this.state.current.getMonth() ===
                                                    MonthNames[month]
                                                        ? "current"
                                                        : ""
                                                }${
                                                    this.props.value?.getMonth() ===
                                                    MonthNames[month]
                                                        ? " selected"
                                                        : ""
                                                }${
                                                    (this.props.minDate &&
                                                        new Date(
                                                            this.props.value.getFullYear(),
                                                            MonthNames[month],
                                                            1,
                                                        )
                                                            .addMonths(1)
                                                            .addDays(-1) <
                                                            this.props
                                                                .minDate) ||
                                                    (this.props.maxDate &&
                                                        new Date(
                                                            this.props.value.getFullYear(),
                                                            MonthNames[month],
                                                            1,
                                                        ).addDays(-1) >
                                                            this.props.maxDate)
                                                        ? " disabled"
                                                        : ""
                                                }`}
                                                onFocus={this.handleFocus.bind(
                                                    this,
                                                )}
                                                onKeyUpCapture={event =>
                                                    event.key === "Enter" &&
                                                    this.setDisplayMonth(
                                                        event,
                                                        MonthNames[month],
                                                    )
                                                }
                                                onClickCapture={event =>
                                                    this.setDisplayMonth(
                                                        event,
                                                        MonthNames[month],
                                                    )
                                                }>
                                                {month}
                                            </li>
                                        ))}
                                </ul>
                            </div>
                            <div
                                className={`calendar-years${
                                    this.state.setYear ? " open" : ""
                                }`}>
                                <ul className="years">
                                    {Array(200)
                                        .fill(
                                            this.props.value
                                                ? this.props.value.getFullYear() -
                                                      125
                                                : new Date().getFullYear() -
                                                      125,
                                        )
                                        .map((year, index) => (
                                            <li
                                                key={index}
                                                tabIndex={0}
                                                ref={
                                                    this.props.value?.getFullYear() ===
                                                    year + index
                                                        ? this.state.yearRef
                                                        : null
                                                }
                                                className={`${
                                                    this.state.current.getFullYear() ===
                                                    year + index
                                                        ? "current"
                                                        : ""
                                                }${
                                                    this.props.value?.getFullYear() ===
                                                    year + index
                                                        ? " selected"
                                                        : ""
                                                }${
                                                    (this.props.minDate &&
                                                        new Date(
                                                            year + index,
                                                            11,
                                                            31,
                                                        ) <
                                                            this.props
                                                                .minDate) ||
                                                    (this.props.maxDate &&
                                                        new Date(
                                                            year + index,
                                                            0,
                                                            1,
                                                        ).addDays(-1) >
                                                            this.props.maxDate)
                                                        ? " disabled"
                                                        : ""
                                                }`}
                                                onFocus={this.handleFocus.bind(
                                                    this,
                                                )}
                                                onKeyUpCapture={event =>
                                                    event.key === "Enter" &&
                                                    this.setDisplayedYear(
                                                        event,
                                                        year + index,
                                                    )
                                                }
                                                onClickCapture={event =>
                                                    this.setDisplayedYear(
                                                        event,
                                                        year + index,
                                                    )
                                                }>
                                                {year + index}
                                            </li>
                                        ))}
                                </ul>
                            </div>
                        </div>,
                        document.querySelector(".page-elements"),
                    )}
            </label>
        );
    }
}

export default DatePicker;
